import "./unifiedDashStyles.scss";
import { isValidNum } from "../Telemetry/Constant";
import {
  chart_parsed_data,
  division_mock,
  geoJsonMap,
  mock_parsed_data,
  nav_filter_severity_config,
  station_mock,
} from "./unifiedAlarmDashboardConstants";
import UnifiedDashboardMap from "./UnifiedDashboardMap";
import { getParsedLocalStorage } from "../../Utils/storage-utils";
import { extractParsedDataFromPermissions } from "../../Utils/Utils";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isLoggedInSelector, selectedCustomersSelector } from "../../features/authentication/auth-slice";
import axios from "axios";
import { FaFileExcel, FaFilePdf, FaExpand, FaCompress } from "react-icons/fa";
import { exportToExcel, exportToPDF } from "./utils/unifiedDashExport";
import {
  ALARM_WEBSOCKET_PATH,
  APP_BASE_URL,
  APP_BASE_URL_ALARM,
  APP_BASE_URL_ALARM_SOCKET,
  ASSET_COUNTS,
  GET_ACTIVE_ALARMS,
  GET_ASSET_HEALTH,
  GET_MAINTENANCE_SCHEDULE,
  SYSTEM_ALARMS,
} from "../../Common/EndPoints";
import { reduce } from "rxjs";
import { useImmer } from "use-immer";
import OverlayLoader from "../overlay-loader/OverlayLoader";
import {
  setAlarmFilters,
  setTemporaryAlarmFilters,
  updateTemporaryAlarmFilters,
} from "../../features/Alarms/alarmsSlice";
import { useNavigate } from "react-router-dom";
import { updateSelectedSidebarKey } from "../NewSidebar/sidebarSlice";
import { useAlarmsWebsocket } from "../../features/Alarms/useAlarmsWebsocket";
import AlarmBarChart from "./DummyBarChart";
import MapWithGeoJSON from "./MapWithGeoJSON";
import SytemAarmChart from "./UnifiedComps/SytemAarmChart";
import AlarmCard from "./components/AlarmCard";
import UnifiedStatCards from "./components/UnifiedStatCards";
import {
  AlarmDistributionChart,
  TopStationsChart,
  DivisionAlarmsChart,
  DivisionAlarmGrid,
  StationAlarmGrid,
  SystemAlarmsRadialChart,
} from "./components/UnifiedCharts";

// const color_pallet_keys =['pf','fl','mf', 'mo']

const color_pallet_keys = [
  { key: "pf", label: "Predictive Failure" },
  { key: "fl", label: "Failure" },
  { key: "mf", label: "Abnormal Maintenanace" },
  { key: "mo", label: "Maintenanace Overdue" },
];

const convertCountToText = (_cnt, limit = 99) => {
  if (!isValidNum(_cnt)) return "";
  let strCnt = _cnt;
  let limtStrLen = `${limit}`.length;
  if (_cnt > limit) {
    strCnt = `${limit}+`;
  } else {
    strCnt = `${_cnt}`.padStart(limtStrLen, "0");
  }
  return strCnt;
};

const initParsedAlarmCntPacket = {
  division_wise: {},
  station_wise: {},
  system_wise: {},
};

const getStrCustomerIds = (list = []) => {
  return list.map((customer) => customer.customerId).join(",");
};
const getStrCustomerNames = (list = []) => {
  return list.map((customer) => customer.customerName).join(",");
};
function UnifiedAlarmDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user_permissions = getParsedLocalStorage("permissions");
  const parsed_permissions = useMemo(() => {
    return extractParsedDataFromPermissions(user_permissions);
  }, []);
  const selectedCustomersList = useSelector(selectedCustomersSelector);
  const [selectedDivisionTab, setSelectedDivisionTab] = useState(null);
  const [selectedStationTab, setSelectedStationTab] = useState(null);
  const [parsedAlarmCnts, setParsedAlarmCnts] = useImmer(initParsedAlarmCntPacket);
  const [filteredParsedAlarmCnts, setFilteredParsedAlarmCnts] = useImmer(initParsedAlarmCntPacket);
  const [pendingRequestCount, setPendingRequestCount] = useImmer(0);
  const [alarmSocketPacket, setAlarmSocketPacket] = useState(null);
  const [fmsAlarmCnt, setFmsAlarmCnt] = useState(0);
  const [fullscreenWidget, setFullscreenWidget] = useState(null); // 'division' | 'system' | 'station' | 'map' | null

  // Toggle fullscreen for a widget
  const toggleFullscreen = useCallback((widgetName) => {
    setFullscreenWidget((prev) => (prev === widgetName ? null : widgetName));
  }, []);

  const isLoading = useMemo(() => {
    return pendingRequestCount > 0;
  }, [pendingRequestCount]);

  const hasData = useMemo(() => {
    return Object.keys(parsedAlarmCnts?.station_wise ?? {})?.length > 0;
  }, [parsedAlarmCnts]);

  const allowed_stations_parsed_perm = useMemo(()=>{
        const strCustomerNames = getStrCustomerNames(selectedCustomersList);
return parsed_permissions?.stations.filter((val)=>strCustomerNames.includes(val.value))
  },[selectedCustomersList])
  console.log('allowed_stations_parsed_perm',allowed_stations_parsed_perm)

  // Calculate statistics for stat cards
  const calculatedStats = useMemo(() => {
    const stationData = filteredParsedAlarmCnts?.station_wise ?? {};
    const stationValues = Object.values(stationData);

    let totalPf = 0, totalFl = 0, totalMo = 0, totalMf = 0;
    let affectedStations = 0;

    stationValues.forEach((station) => {
      totalPf += station.pf_cnt ?? 0;
      totalFl += station.fl_cnt ?? 0;
      totalMo += station.mo_cnt ?? 0;
      totalMf += station.mf_cnt ?? 0;

      const stationTotal = (station.pf_cnt ?? 0) + (station.fl_cnt ?? 0) + (station.mo_cnt ?? 0) + (station.mf_cnt ?? 0);
      if (stationTotal > 0) affectedStations++;
    });

    const totalStations = selectedCustomersList?.length ?? 0;

    return {
      totalAlarms: totalPf + totalFl + totalMo + totalMf,
      predictiveFailure: totalPf,
      failure: totalFl,
      maintenanceOverdue: totalMo,
      maintenanceDeficiency: totalMf,
      affectedStations,
      healthyStations: Math.max(0, totalStations - affectedStations),
      totalStations,
    };
  }, [filteredParsedAlarmCnts?.station_wise, selectedCustomersList]);

  // Calculate total alarm counts for distribution chart
  const totalAlarmCounts = useMemo(() => {
    const stationData = filteredParsedAlarmCnts?.station_wise ?? {};
    const stationValues = Object.values(stationData);

    let pf_cnt = 0, fl_cnt = 0, mo_cnt = 0, mf_cnt = 0;
    stationValues.forEach((station) => {
      pf_cnt += station.pf_cnt ?? 0;
      fl_cnt += station.fl_cnt ?? 0;
      mo_cnt += station.mo_cnt ?? 0;
      mf_cnt += station.mf_cnt ?? 0;
    });

    return { pf_cnt, fl_cnt, mo_cnt, mf_cnt };
  }, [filteredParsedAlarmCnts?.station_wise]);

  useEffect(() => {
    if (!selectedCustomersList.length) return;
    let socket;
    const handelWebsocketAlarmMessage = (alarm_packet) => {
      if (alarm_packet.clear_dt) {
        getUnifiedDashData(true);
        setAlarmSocketPacket(alarm_packet);
      }
      if (alarm_packet.created_dt && alarm_packet.is_active && !alarm_packet.updated_dt) {
        getUnifiedDashData(true);
        setAlarmSocketPacket(alarm_packet);
      }
    };
    function websocketConnect() {
      const strCustomerIds = getStrCustomerIds(selectedCustomersList);
      const url = `${APP_BASE_URL_ALARM_SOCKET + ALARM_WEBSOCKET_PATH}/ALARMS?customer_ids=${strCustomerIds}`;
      socket = new WebSocket(url);
      socket.onmessage = (event) => {
        const alarm = JSON.parse(event.data);
        if (!alarm) return;
        handelWebsocketAlarmMessage(alarm);
      };

      socket.onerror = function () {
        socket.close();
      };
    }
    websocketConnect();
    return () => {
      if (socket) {
        socket.close();
        socket = null;
      }
    };
  }, [selectedCustomersList]);

  useEffect(() => {
    getUnifiedDashData();
  }, [selectedCustomersList]);

  const getUnifiedDashData = async (with_silent_update = false) => {
    const strCustomerIds = getStrCustomerIds(selectedCustomersList);
    const setCustomerNames = getStrCustomerNames(selectedCustomersList);
    const singleCustomer = selectedCustomersList.length === 1 ? selectedCustomersList[0] : null;

    const endpoints = [
      { url: `${APP_BASE_URL_ALARM}${SYSTEM_ALARMS}`, params: { customer_ids: strCustomerIds } },
      { url: `${APP_BASE_URL}${GET_MAINTENANCE_SCHEDULE}`, params: { customerIds: strCustomerIds } },
      { url: `${APP_BASE_URL}${GET_ASSET_HEALTH}`, params: { customerIds: strCustomerIds } },
      {
        url: `${APP_BASE_URL}${ASSET_COUNTS}`,
        params: { customerType: "STNCODE", requestNames: setCustomerNames },
      },
    ];
    const fetchAlarmCountsData = async () => {
      startRequest(with_silent_update);
      try {
        const results = await Promise.allSettled(endpoints.map(({ url, params }) => axios.get(url, { params })));
        console.log("results", results);
        const mergedStationAlarms = {};
        const system_wise_alarm = {};

        results.forEach((result, index) => {
          if (result.status !== "fulfilled") return;

          const data = result.value?.data?.data ?? result.value?.data ?? [];

          if (index === 0) handleAlarmCounts(data, mergedStationAlarms, system_wise_alarm);
          if (index === 1) handleOverdueCounts(data, mergedStationAlarms, singleCustomer);
          if (index === 2) handleAssetHealthCounts(data, mergedStationAlarms, singleCustomer);
          if (index === 3) handleTotalCounts(data, mergedStationAlarms);
        });

        const mergedDivisionAlarms = getDivisionWiseCounts(mergedStationAlarms, allowed_stations_parsed_perm ?? []);

        setParsedAlarmCnts((prev) => ({
          ...prev,
          station_wise: mergedStationAlarms,
          division_wise: mergedDivisionAlarms,
          system_wise: system_wise_alarm,
        }));
        setFilteredParsedAlarmCnts((prev) => ({
          ...prev,
          station_wise: mergedStationAlarms,
          division_wise: mergedDivisionAlarms,
          system_wise: system_wise_alarm,
        }));
      } catch (error) {
        console.error(error);
      } finally {
        endRequest(with_silent_update);
      }
    };

    fetchAlarmCountsData();
    const alarm_asset_api_params = {
      customer_ids: strCustomerIds,
      with_device: true,
      with_device_type: true,
      with_software: true,
    };
    // fetchActiveAlarmsAssets(alarm_asset_api_params, with_silent_update);
  };

  useEffect(() => {
    const params = {};
    const filtered_parsed_counts = JSON.parse(JSON.stringify(parsedAlarmCnts));
    console.log("filtered_parsfgfdged_counts", filtered_parsed_counts);

    if (selectedDivisionTab || selectedStationTab) {
      if (selectedStationTab) {
        const prev_system_data = filtered_parsed_counts.system_wise;
        const filtered_system_data = {};
        const allowed_stations = [selectedStationTab];
        for (const sys_code in prev_system_data) {
          const system_data = prev_system_data[sys_code];
          const counts = {};
          allowed_stations.forEach((stn) => {
            counts[stn] = system_data?.counts?.[stn] ?? 0;
          });
          filtered_system_data[sys_code] = {
            ...system_data,
            counts: counts,
          };
        }

        filtered_parsed_counts.system_wise = filtered_system_data;
      } else if (selectedDivisionTab) {
        const allowed_stations = parsed_permissions?.stations
          ?.filter(
            (obj) =>
              obj.division.id === selectedDivisionTab &&
              selectedCustomersList.find((customerObj) => customerObj.customerName === obj.value),
          )
          .map((obj) => obj.value);

        const prev_stn_data = filtered_parsed_counts.station_wise;
        const filtered_stn_data = {};
        const prev_system_data = filtered_parsed_counts.system_wise;
        const filtered_system_data = {};

        for (const stn_code in prev_stn_data) {
          if (allowed_stations.includes(stn_code)) {
            filtered_stn_data[stn_code] = prev_stn_data[stn_code];
          }
        }

        for (const sys_code in prev_system_data) {
          const system_data = prev_system_data[sys_code];
          const counts = {};
          allowed_stations.forEach((stn) => {
            counts[stn] = system_data?.counts?.[stn] ?? 0;
          });
          filtered_system_data[sys_code] = {
            ...system_data,
            counts: counts,
          };
        }
        filtered_parsed_counts.station_wise = filtered_stn_data;
        filtered_parsed_counts.system_wise = filtered_system_data;
      }
    }
    setFilteredParsedAlarmCnts(filtered_parsed_counts);
  }, [selectedDivisionTab, selectedStationTab]);

  const startRequest = (with_silent_update) => {
    if (!with_silent_update) {
      setPendingRequestCount((count) => count + 1);
    }
  };

  const endRequest = (with_silent_update) => {
    if (!with_silent_update) {
      setPendingRequestCount((count) => Math.max(0, count - 1));
    }
  };

  const handleAlarmCounts = (data, alarmMap, system_wise_alarm) => {
    data.forEach(({ status = {}, customer_name, software_code, software_alias, software_id }) => {
      const pf_cnt = status.warning ?? 0;
      const fl_cnt = status.critical ?? 0;
      const total_alarms = pf_cnt + fl_cnt;
      if (!system_wise_alarm[software_code]) {
        system_wise_alarm[software_code] = {
          software_id: software_id,
          software_code: software_code,
          software_alias: software_alias,
          counts: {},
        };
      }
      system_wise_alarm[software_code].counts = {
        ...system_wise_alarm[software_code].counts,
        [customer_name]: (system_wise_alarm[software_code].counts?.[customer_name] ?? 0) + total_alarms,
      };

      if (pf_cnt + fl_cnt > 0) {
        if (!alarmMap[customer_name]) {
          alarmMap[customer_name] = { customer_name, pf_cnt: 0, fl_cnt: 0, mf_cnt: 0, mo_cnt: 0, total_asset_cnt: 0 };
        }
        alarmMap[customer_name].pf_cnt += pf_cnt;
        alarmMap[customer_name].fl_cnt += fl_cnt;
      }
    });
  };

  const handleOverdueCounts = (data, alarmMap, singleCustomer) => {
    let list = data;

    if (singleCustomer && list.length) {
      const total = list.reduce((sum, item) => sum + (item.overdue || 0), 0);
      list = [{ overdue: total, customerName: singleCustomer.customerName }];
    }

    list.forEach(({ customerName, overdue = 0 }) => {
      if (overdue > 0) {
        if (!alarmMap[customerName]) {
          alarmMap[customerName] = {
            customer_name: customerName,
            pf_cnt: 0,
            fl_cnt: 0,
            mf_cnt: 0,
            mo_cnt: 0,
            total_asset_cnt: 0,
          };
        }
        alarmMap[customerName].mo_cnt += overdue;
      }
    });
  };
  const handleTotalCounts = (data, alarmMap) => {
    data.forEach(({ stncode, count = 0 }) => {
      if (alarmMap[stncode]) {
        alarmMap[stncode].total_asset_cnt = count;
      }
    });
  };
  const handleAssetHealthCounts = (data, alarmMap, singleCustomer) => {
    let list = data;
    if (singleCustomer && list.length) {
      const totals = list.reduce(
        (acc, { warningCount = 0, criticalCount = 0 }) => ({
          warningCount: acc.warningCount + warningCount,
          criticalCount: acc.criticalCount + criticalCount,
        }),
        { warningCount: 0, criticalCount: 0 },
      );
      list = [{ customerName: singleCustomer.customerName, ...totals }];
    }

    list.forEach(({ customerName, criticalCount = 0, warningCount = 0 }) => {
      const total = criticalCount + warningCount;
      if (total > 0) {
        if (!alarmMap[customerName]) {
          alarmMap[customerName] = {
            customer_name: customerName,
            pf_cnt: 0,
            fl_cnt: 0,
            mf_cnt: 0,
            mo_cnt: 0,
            total_asset_cnt: 0,
          };
        }
        alarmMap[customerName].mf_cnt += total;
      }
    });
  };

  const getDivisionWiseCounts = (stationMap, stations) => {
    console.log('sjlkdjasl', stationMap, stations, selectedCustomersList)
    const divisionMap = {};
    const total_allowed_divs = stations.reduce((acc, val) => {
      if (!acc.includes(val.division.value)) {
        acc.push(val.division.value);
      }
      return acc;
    }, []);

    stations.forEach(({ value: stationName, division }) => {
      const alarm = stationMap[stationName];
      if (!alarm) return;

      const div_code = division?.value;
      if (!div_code) return;

      if (!divisionMap[div_code]) {
        divisionMap[div_code] = { div_code, pf_cnt: 0, fl_cnt: 0, mf_cnt: 0, mo_cnt: 0, total_asset_cnt: 0 };
      }

      divisionMap[div_code].pf_cnt += alarm.pf_cnt;
      divisionMap[div_code].fl_cnt += alarm.fl_cnt;
      divisionMap[div_code].mf_cnt += alarm.mf_cnt;
      divisionMap[div_code].mo_cnt += alarm.mo_cnt;
      divisionMap[div_code].total_asset_cnt += alarm.total_asset_cnt;
    });
    total_allowed_divs.forEach((div) => {
      if (!divisionMap[div]) {
        divisionMap[div] = { div_code: div, pf_cnt: 0, fl_cnt: 0, mf_cnt: 0, mo_cnt: 0, total_asset_cnt: 0 };
      }
    });

    return divisionMap;
  };

  const handleAlarmCardCounClick = (card_type, count_type, card_id, sev_code) => {
    let arams_params = {};
    let maintenance_params = {};
    let customer_id_list = [];
    const selecteIds = selectedCustomersList.map((obj) => obj.customerId);
    if (card_type === "STATION") {
      customer_id_list = parsed_permissions.stations.filter(
        (stn) => stn.value === card_id && selecteIds.includes(stn.id),
      );
    }
    if (card_type === "DIVISION") {
      customer_id_list = parsed_permissions.stations.filter(
        (stn) => stn.division.id === card_id && selecteIds.includes(stn.id),
      );
    }
    if (count_type === "alarm") {
      const sevr = nav_filter_severity_config[sev_code] ?? null;
      arams_params = {
        customer_ids: customer_id_list.map((obj) => ({ id: obj.id, value: obj.value })),
        severity: sevr,
        limit: 20,
        offset: 0,
        is_active: true,
      };
      dispatch(updateSelectedSidebarKey("ALARM"));
      dispatch(updateTemporaryAlarmFilters(arams_params));
      navigate("/alarms");
    }
    if (count_type === "mis") {
      let url = "mis-reports";
      const filter_sev_code = nav_filter_severity_config[sev_code] ?? null;
      if (filter_sev_code) {
        url = `${url}?selected_tab=${filter_sev_code}&stn_codes=${customer_id_list.map((stn) => stn.value).toString()}`;
        if(filter_sev_code === "MAINTENANCE_DEFICIENCY") {
          url += `&assetHealth=Unhealthy`
        }
        dispatch(updateSelectedSidebarKey("REPORT*MIS_REPORTS"));
        navigate(url);
      }
    }
  };

  // Handler for stat card clicks - navigates to alarm page with severity filter
  const handleStatCardClick = (cardType) => {
    const selecteIds = selectedCustomersList.map((obj) => obj.customerId);
    const customer_id_list = parsed_permissions.stations.filter(
      (stn) => selecteIds.includes(stn.id),
    );

    // For MO (maintenance overdue) - redirect to Assets > Maintenance > Overdue tab
    if (cardType === "mo") {
      dispatch(updateSelectedSidebarKey("ASSET*ASSET_MANAGEMENT"));
      navigate("/assets?tab=Maintenance&subtab=Overdue");
      return;
    }

    // For MF (maintenance deficiency) - redirect to MIS reports
    if (cardType === "mf") {
      const url = `mis-reports?selected_tab=MAINTENANCE_DEFICIENCY&stn_codes=${customer_id_list.map((stn) => stn.value).toString()}&assetHealth=Unhealthy`;
      dispatch(updateSelectedSidebarKey("REPORT*MIS_REPORTS"));
      navigate(url);
      return;
    }

    let severity = null;
    if (cardType === "pf") severity = "WARNING";
    else if (cardType === "fl") severity = "CRITICAL";
    else if (cardType === "total") severity = null; // All alarms

    const alarm_params = {
      customer_ids: customer_id_list.map((obj) => ({ id: obj.id, value: obj.value })),
      severity: severity,
      limit: 20,
      offset: 0,
      is_active: true,
    };

    dispatch(updateSelectedSidebarKey("ALARM"));
    dispatch(updateTemporaryAlarmFilters(alarm_params));
    navigate("/alarms");
  };

  // Handler for division count clicks - navigates to alarm/MIS page with division and severity filter
  const handleDivisionCountClick = (divCode, sevType) => {
    const selecteIds = selectedCustomersList.map((obj) => obj.customerId);
    const divisionData = parsed_permissions.stations.find((stn) => stn.division?.value === divCode);
    const divisionId = divisionData?.division?.id;

    const customer_id_list = parsed_permissions.stations.filter(
      (stn) => stn.division?.value === divCode && selecteIds.includes(stn.id),
    );

    // For MO - redirect to Assets > Maintenance > Overdue tab
    if (sevType === "mo") {
      dispatch(updateSelectedSidebarKey("ASSET*ASSET_MANAGEMENT"));
      navigate("/assets?tab=Maintenance&subtab=Overdue");
      return;
    }

    // For MF types, redirect to MIS reports
    if (sevType === "mf") {
      const filter_sev_code = nav_filter_severity_config[sevType];
      let url = `mis-reports?selected_tab=${filter_sev_code}&stn_codes=${customer_id_list.map((stn) => stn.value).toString()}&assetHealth=Unhealthy`;
      dispatch(updateSelectedSidebarKey("REPORT*MIS_REPORTS"));
      navigate(url);
      return;
    }

    const severity = nav_filter_severity_config[sevType] ?? null;
    const alarm_params = {
      customer_ids: customer_id_list.map((obj) => ({ id: obj.id, value: obj.value })),
      severity: severity,
      limit: 20,
      offset: 0,
      is_active: true,
    };

    dispatch(updateSelectedSidebarKey("ALARM"));
    dispatch(updateTemporaryAlarmFilters(alarm_params));
    navigate("/alarms");
  };

  // Handler for station count clicks - navigates to alarm/MIS page with station and severity filter
  const handleStationCountClick = (stnCode, sevType) => {
    const selecteIds = selectedCustomersList.map((obj) => obj.customerId);
    const customer_id_list = parsed_permissions.stations.filter(
      (stn) => stn.value === stnCode && selecteIds.includes(stn.id),
    );

    // For MO - redirect to Assets > Maintenance > Overdue tab
    if (sevType === "mo") {
      dispatch(updateSelectedSidebarKey("ASSET*ASSET_MANAGEMENT"));
      navigate("/assets?tab=Maintenance&subtab=Overdue");
      return;
    }

    // For MF types, redirect to MIS reports
    if (sevType === "mf") {
      const filter_sev_code = nav_filter_severity_config[sevType];
      let url = `mis-reports?selected_tab=${filter_sev_code}&stn_codes=${stnCode}&assetHealth=Unhealthy`;
      dispatch(updateSelectedSidebarKey("REPORT*MIS_REPORTS"));
      navigate(url);
      return;
    }

    const severity = nav_filter_severity_config[sevType] ?? null;
    const alarm_params = {
      customer_ids: customer_id_list.map((obj) => ({ id: obj.id, value: obj.value })),
      severity: severity,
      limit: 20,
      offset: 0,
      is_active: true,
    };

    dispatch(updateSelectedSidebarKey("ALARM"));
    dispatch(updateTemporaryAlarmFilters(alarm_params));
    navigate("/alarms");
  };

  // Export handlers
  const handleExportToExcel = useCallback(() => {
    const exportData = {
      stats: calculatedStats,
      divisions: filteredParsedAlarmCnts?.division_wise,
      stations: filteredParsedAlarmCnts?.station_wise,
      systems: filteredParsedAlarmCnts?.system_wise,
    };
    exportToExcel(exportData, "unified_dashboard");
  }, [calculatedStats, filteredParsedAlarmCnts]);

  const handleExportToPDF = useCallback(() => {
    const exportData = {
      stats: calculatedStats,
      divisions: filteredParsedAlarmCnts?.division_wise,
      stations: filteredParsedAlarmCnts?.station_wise,
      systems: filteredParsedAlarmCnts?.system_wise,
    };
    exportToPDF(exportData, "unified_dashboard");
  }, [calculatedStats, filteredParsedAlarmCnts]);


  return (
    <div className="unified-dash-main-container">
      <div className="unified-header-container app--custome--card">
        <div className="alarm-color-pallet">
          {color_pallet_keys.map((pallet_key_obj) => {
            const { key, label } = pallet_key_obj;
            return (
              <div key={key} className={`${key}-alarm-background pallet-card`}>
                {key.toUpperCase()} - {label}
              </div>
            );
          })}
        </div>
        <div className="export-buttons">
          <button
            className="export-btn excel-btn"
            onClick={handleExportToExcel}
            disabled={isLoading || !hasData}
            title="Export to Excel"
          >
            <FaFileExcel /> Excel
          </button>
          <button
            className="export-btn pdf-btn"
            onClick={handleExportToPDF}
            disabled={isLoading || !hasData}
            title="Export to PDF"
          >
            <FaFilePdf /> PDF
          </button>
        </div>
      </div>

      {/* Summary Stat Cards */}
      <UnifiedStatCards stats={calculatedStats} onCardClick={handleStatCardClick} />

      {/* Charts Row */}
      <div className="unified-charts-row">
        <div className="unified-chart-container app--custome--card">
          <AlarmDistributionChart data={totalAlarmCounts} />
        </div>
        <div className="unified-chart-container app--custome--card">
          <TopStationsChart stationData={filteredParsedAlarmCnts?.station_wise} />
        </div>
        <div className="unified-chart-container app--custome--card">
          <DivisionAlarmsChart divisionData={filteredParsedAlarmCnts?.division_wise} />
        </div>
      </div>

      <div className={`unified-division-container app--custome--card unified-widget-card ${fullscreenWidget === 'division' ? 'fullscreen' : ''}`}>
        <div className="unified-widget-header">
          <div className="unified-widget-header-label">Division Alarms</div>
          <button className="fullscreen-btn" onClick={() => toggleFullscreen('division')} title={fullscreenWidget === 'division' ? 'Exit Fullscreen' : 'Fullscreen'}>
            {fullscreenWidget === 'division' ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
        <div className="unified-widget-container non-gis division-grid-view">
          {isLoading && <div className="loading-text">Loading...</div>}
          {!isLoading && hasData && (
            <DivisionAlarmGrid
              divisionData={filteredParsedAlarmCnts?.division_wise}
              selectedDivision={selectedDivisionTab}
              onDivisionClick={(div) => {
                if (selectedDivisionTab === div.code) {
                  setSelectedDivisionTab(null);
                } else {
                  setSelectedDivisionTab(div.code);
                }
              }}
              onCountClick={handleDivisionCountClick}
            />
          )}
          {!isLoading && !hasData && (
            <div className="all-assets-are-healthy">
              <span>All Assets are healthy ✓</span>
            </div>
          )}
        </div>
      </div>
      <div className={`unified-system-dash app--custome--card unified-widget-card ${fullscreenWidget === 'system' ? 'fullscreen' : ''}`}>
        <div className="unified-widget-header">
          <div className="unified-widget-header-label">System Alarms</div>
          <button className="fullscreen-btn" onClick={() => toggleFullscreen('system')} title={fullscreenWidget === 'system' ? 'Exit Fullscreen' : 'Fullscreen'}>
            {fullscreenWidget === 'system' ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
        <div className="unified-widget-container non-gis system-chart-view">
          {isLoading && <div className="loading-text">Loading...</div>}
          {!isLoading && (
            <SystemAlarmsRadialChart
              systemData={filteredParsedAlarmCnts?.system_wise}
              onSystemClick={(system) => {
                const alarm_params = {
                  systems: [{ systemId: system.id, systemName: system.code }],
                  limit: 20,
                  offset: 0,
                  is_active: true,
                };
                dispatch(updateTemporaryAlarmFilters(alarm_params));
                navigate("/alarms");
              }}
            />
          )}
        </div>
      </div>
      <div className={`unified-station-container app--custome--card unified-widget-card ${fullscreenWidget === 'station' ? 'fullscreen' : ''}`}>
        <div className="unified-widget-header">
          <div className="unified-widget-header-label">Station Alarms</div>
          <button className="fullscreen-btn" onClick={() => toggleFullscreen('station')} title={fullscreenWidget === 'station' ? 'Exit Fullscreen' : 'Fullscreen'}>
            {fullscreenWidget === 'station' ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
        <div className="unified-widget-container non-gis station-grid-view">
          {isLoading && <div className="loading-text">Loading...</div>}
          {!isLoading && hasData && (
            <StationAlarmGrid
              stationData={filteredParsedAlarmCnts?.station_wise}
              selectedStation={selectedStationTab}
              onStationClick={(stn) => {
                if (selectedStationTab === stn.code) {
                  setSelectedStationTab(null);
                } else {
                  setSelectedStationTab(stn.code);
                }
              }}
              onCountClick={handleStationCountClick}
            />
          )}
          {!isLoading && !hasData && (
            <div className="all-assets-are-healthy">
              <span>All Assets are healthy ✓</span>
            </div>
          )}
        </div>
      </div>
      <div className={`unified-map-container app--custome--card unified-widget-card ${fullscreenWidget === 'map' ? 'fullscreen' : ''}`}>
        <div className="unified-widget-header unified-widget-header__gis">
          <div className="unified-widget-header-label">GIS Cable Network And Alarms</div>
          <div className="alarm-cnt">{fmsAlarmCnt ? `Total Alarm Assets : ${fmsAlarmCnt}` : "No Alarms"}</div>
          <button className="fullscreen-btn" onClick={() => toggleFullscreen('map')} title={fullscreenWidget === 'map' ? 'Exit Fullscreen' : 'Fullscreen'}>
            {fullscreenWidget === 'map' ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
        <div className="unified-widget-container gis">
          <UnifiedDashboardMap
            alarmSocketPacket={alarmSocketPacket}
            setFmsAlarmCnt={setFmsAlarmCnt}
            isFullscreen={fullscreenWidget === "map"}
          />
        </div>
      </div>
      {isLoading && <OverlayLoader />}
    </div>
  );
}

export default UnifiedAlarmDashboard;
